import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { questionResponseService } from 'src/data/api/helios-service/questionResponseService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  UpdateAuditQuestionResponseRequest,
  ListAuditQuestionResponsesRequest,
  GetAuditQuestionResponseRequest,
  AuditQuestionItemResponse,
} from 'src/types/helios/QuestionResponse';
import {
  AuditQuestions,
  SearchAuditQuestionsRequest,
  SearchAuditQuestionsResponse,
} from 'src/types/helios/SearchAuditQuestions';
import { handleError } from 'src/utils/generalUtils';
import { sampleAuditQuestions } from 'src/utils/mockdata';

export interface QuestionResponseState {
  auditQuestionsData: SearchAuditQuestionsResponse;
  searchQuestionsApiStatus: ApiRequestStatus;
  updateQuestionResponseApiStatus: ApiRequestStatus;
  updateResponseErrorCode: number | null;
  getAllApiStatus: ApiRequestStatus;
  getByIdApiStatus: ApiRequestStatus;
  auditQuestionResponses: AuditQuestionItemResponse[];
  currentQuestionIndex: number;
  filteredQuestionIds: string[] | null; // null means no filter applied, show all
}

const initialState: QuestionResponseState = {
  auditQuestionResponses: [],
  searchQuestionsApiStatus: initApiStatus,
  updateQuestionResponseApiStatus: initApiStatus,
  updateResponseErrorCode: null,
  getAllApiStatus: initApiStatus,
  getByIdApiStatus: initApiStatus,
  currentQuestionIndex: 0,
  filteredQuestionIds: null,
  auditQuestionsData: {
    auditMetadata: {
      auditId: '',
      businessUnit: '',
      divisionName: '',
      status: '',
      auditType: '',
      name: '',
      location: '',
      scheduledDate: 0,
      totalQuestions: 0,
      templateId: '',
      forceClosed: false,
      isDeleted: false,
      createdAt: 0,
      createdBy: '',
      expiryDate: 0,
      metrics: {
        totalQuestions: 0,
        completedQuestions: 0,
        passedQuestions: 0,
        failedQuestions: 0,
        naQuestions: 0,
        scorePercent: 0,
      },
    },
    questions: [],
  },
};

export const searchAuditQuestionsApiThunk = createAsyncThunk(
  'questionResponse/searchAuditQuestions',
  async (data: SearchAuditQuestionsRequest, { rejectWithValue }) => {
    try {
      const response = await questionResponseService.search(data);

      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateQuestionResponseApiThunk = createAsyncThunk(
  'questionResponse/updateQuestionResponse',
  async (data: UpdateAuditQuestionResponseRequest, { rejectWithValue }) => {
    try {
      const response = await questionResponseService.update(data);
      return response;
    } catch (error: unknown) {
      const errorResponse = error as any;
      if (errorResponse?.status === 409) {
        const modifiedBy = errorResponse?.data?.lastModifiedBy || 'another user';
        return rejectWithValue({
          message: `This question was modified by ${modifiedBy}. Please dismiss this message and try again.`,
          code: 409,
        });
      }
      return rejectWithValue(handleError(error));
    }
  },
);

export const getAllQuestionResponsesApiThunk = createAsyncThunk(
  'questionResponse/getAllQuestionResponses',
  async (data: ListAuditQuestionResponsesRequest, { rejectWithValue }) => {
    try {
      const response = await questionResponseService.getAll(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getQuestionResponseByIdApiThunk = createAsyncThunk(
  'questionResponse/getQuestionResponseById',
  async (data: GetAuditQuestionResponseRequest, { rejectWithValue }) => {
    try {
      const response = await questionResponseService.getById(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

/**
 * Silent poll thunk — fetches all question responses without clearing state or showing loading.
 * Used by the poller to detect changes from other users without disrupting the UI.
 */
export const pollQuestionResponsesApiThunk = createAsyncThunk(
  'questionResponse/pollQuestionResponses',
  async (data: ListAuditQuestionResponsesRequest, { rejectWithValue }) => {
    try {
      const response = await questionResponseService.getAll(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const questionResponsesSlice = createSlice({
  name: 'questionResponses',
  initialState,
  reducers: {
    resetApiStatus: (state) => {
      state.searchQuestionsApiStatus = initApiStatus;
      state.updateQuestionResponseApiStatus = initApiStatus;
      state.updateResponseErrorCode = null;
      state.getAllApiStatus = initApiStatus;
      state.getByIdApiStatus = initApiStatus;
    },
    setCurrentQuestionIndex: (state, action: PayloadAction<number>) => {
      state.currentQuestionIndex = action.payload;
    },
    setFilteredQuestionIds: (state, action: PayloadAction<string[] | null>) => {
      state.filteredQuestionIds = action.payload;
    },
    updateQuestionResponseState: (
      state,
      action: PayloadAction<AuditQuestionItemResponse>,
    ) => {
      const index = state.auditQuestionResponses.findIndex(
        (item) => item.questionId === action.payload.questionId,
      );
      if (index !== -1) {
        state.auditQuestionResponses[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchAuditQuestionsApiThunk.pending, (state) => {
        state.searchQuestionsApiStatus.isLoading = true;
        state.searchQuestionsApiStatus.errorMessage = null;
      })
      .addCase(searchAuditQuestionsApiThunk.fulfilled, (state, action) => {
        state.searchQuestionsApiStatus.isLoading = false;
        state.searchQuestionsApiStatus.isSuccess = true;
        state.auditQuestionsData = action.payload;
        state.auditQuestionsData.questions = action.payload.questions;
      })
      .addCase(searchAuditQuestionsApiThunk.rejected, (state, action) => {
        state.searchQuestionsApiStatus.isLoading = false;
        state.searchQuestionsApiStatus.isSuccess = false;
        state.searchQuestionsApiStatus.errorMessage = action.payload as string;
      })
      //
      .addCase(updateQuestionResponseApiThunk.pending, (state, action) => {
        state.updateQuestionResponseApiStatus.isLoading = true;
        state.updateQuestionResponseApiStatus.errorMessage = null;
      })
      .addCase(updateQuestionResponseApiThunk.fulfilled, (state, action) => {
        state.updateQuestionResponseApiStatus.isLoading = false;
        state.updateQuestionResponseApiStatus.isSuccess = true;
        questionResponsesSlice.caseReducers.updateQuestionResponseState(state, {
          type: 'updateQuestionResponseState',
          payload: action.payload.auditQuestionResponse,
        });
      })
      .addCase(updateQuestionResponseApiThunk.rejected, (state, action) => {
        state.updateQuestionResponseApiStatus.isLoading = false;
        state.updateQuestionResponseApiStatus.isSuccess = false;
        const payload = action.payload as any;
        if (typeof payload === 'object' && payload?.code) {
          state.updateQuestionResponseApiStatus.errorMessage = payload.message;
          state.updateResponseErrorCode = payload.code;
        } else {
          state.updateQuestionResponseApiStatus.errorMessage = payload as string;
          state.updateResponseErrorCode = null;
        }
      })
      //
      .addCase(getAllQuestionResponsesApiThunk.pending, (state) => {
        state.auditQuestionResponses = [];
        state.getAllApiStatus.isLoading = true;
        state.getAllApiStatus.errorMessage = null;
      })
      .addCase(getAllQuestionResponsesApiThunk.fulfilled, (state, action) => {
        state.getAllApiStatus.isLoading = false;
        state.getAllApiStatus.isSuccess = true;
        state.auditQuestionResponses = action.payload.auditQuestionResponses;
      })
      .addCase(getAllQuestionResponsesApiThunk.rejected, (state, action) => {
        state.getAllApiStatus.isLoading = false;
        state.getAllApiStatus.isSuccess = false;
        state.getAllApiStatus.errorMessage = action.payload as string;
      })

      //
      .addCase(getQuestionResponseByIdApiThunk.pending, (state) => {
        state.getByIdApiStatus.isLoading = true;
        state.getByIdApiStatus.errorMessage = null;
      })
      .addCase(getQuestionResponseByIdApiThunk.fulfilled, (state, action) => {
        state.getByIdApiStatus.isLoading = false;
        state.getByIdApiStatus.isSuccess = true;
      })
      .addCase(getQuestionResponseByIdApiThunk.rejected, (state, action) => {
        state.getByIdApiStatus.isLoading = false;
        state.getByIdApiStatus.isSuccess = false;
        state.getByIdApiStatus.errorMessage = action.payload as string;
      })

      // Silent poll — only update entries that have changed (no loading state, no clearing)
      .addCase(pollQuestionResponsesApiThunk.fulfilled, (state, action) => {
        const incoming = action.payload.auditQuestionResponses;
        if (!incoming || incoming.length === 0) return;

        // Build a lookup of incoming by questionId for O(1) access
        const incomingMap = new Map<string, AuditQuestionItemResponse>();
        incoming.forEach((r) => incomingMap.set(r.questionId, r));

        let hasChanges = false;

        // Check each existing response against incoming data
        const updated = state.auditQuestionResponses.map((existing) => {
          const fresh = incomingMap.get(existing.questionId);
          if (!fresh) return existing;
          // Only replace if lastUpdatedAt differs
          if (fresh.lastUpdatedAt !== existing.lastUpdatedAt) {
            hasChanges = true;
            return fresh;
          }
          return existing;
        });

        // Only mutate state if something actually changed
        if (hasChanges) {
          state.auditQuestionResponses = updated;
        }
      });
  },
});

export const {
  resetApiStatus,
  setCurrentQuestionIndex,
  setFilteredQuestionIds,
  updateQuestionResponseState,
} = questionResponsesSlice.actions;
export default questionResponsesSlice.reducer;
